import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { debounce } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useAPI from "../../../../api/hooks/useAPI";
import { aiDashboardKeys } from "../../../../api/queryKeys";
import { Toast } from "../../../../components/Toast";
import { defaultStaleTime } from "../../../../utils/constants";
import { GRID_CONFIG } from "../../custom_dashboard/widgetDefinitions";

const DEFAULT_AI_WIDGETS = [
  {
    id: "top-mcp-servers",
    title: "Top MCP Servers by Affected Entities",
    contentType: "component",
    componentType: "TopMCPServersWidget",
    w: 8,
    h: 3.4,
    minH: 3.4,
    minW: 8,
  },
  {
    id: "ai-integrations",
    title: "AI Integrations",
    contentType: "component",
    componentType: "AIIntegrationsWidget",
    w: 4,
    h: 3.4,
    minH: 3.4,
    minW: 4,
  },
  {
    id: "ai-app-usage",
    title: "AI App usage",
    contentType: "component",
    componentType: "AIAppUsageWidget",
    w: 6,
    h: 3.4,
    minH: 3.4,
    minW: 6,
  },
  {
    id: "nhi-table",
    title: "NHI Inventory",
    contentType: "component",
    componentType: "NHITableWidget",
    w: 6,
    h: 3.4,
    minH: 3.4,
    minW: 6,
  },
  {
    id: "nhi-types-distribution",
    title: "NHI Types Distribution",
    contentType: "component",
    componentType: "NHITypesDistribution",
    w: 6,
    h: 3.4,
    minH: 3.4,
    minW: 6,
  },
];

const buildSharedLayouts = (widgets, existingLayouts) => {
  const cols = GRID_CONFIG.cols.lg || 12;
  const saved = new Map(
    (existingLayouts?.lg || []).map((layout) => [layout.i, layout])
  );

  const sharedLayout = widgets.map((widget, index) => {
    const defaultLayout = {
      x: (index * 2) % cols,
      y: Math.floor(index / (cols / 2)) * widget.h,
      w: Math.min(widget.w, cols),
      h: widget.h,
      i: widget.id,
      minH: widget.minH || 2,
      minW: widget.minW || 2,
      maxH: widget.maxH || 30,
      maxW: widget.maxW || 12,
      static: widget.static || false,
    };
    const savedLayout = saved.get(widget.id);
    return savedLayout
      ? { ...defaultLayout, ...savedLayout, i: widget.id }
      : defaultLayout;
  });

  const xsmallLayout = sharedLayout.map((layout) => {
    return {
      ...layout,
      w: Math.max(layout.w, 12),
    };
  });
  const smallLayout = sharedLayout.map((layout) => {
    return {
      ...layout,
      w: Math.max(layout.w, 6),
    };
  });

  return {
    lg: sharedLayout,
    md: sharedLayout,
    sm: smallLayout,
    xs: xsmallLayout,
  };
};

export const useAIDashboard = () => {
  const { saveAIDashboard, loadAIDashboard, trackEvent } = useAPI();
  const queryClient = useQueryClient();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const {
    data: dashboardData,
    isLoading,
    isError,
  } = useQuery({
    queryKey: aiDashboardKeys.all,
    queryFn: loadAIDashboard,
    select: (response) => {
      if (response?.data) {
        const parsed =
          typeof response.data === "string"
            ? JSON.parse(response.data)
            : response.data;
        return parsed;
      }
      const defaultData = {
        widgets: DEFAULT_AI_WIDGETS,
        layouts: {},
      };
      return defaultData;
    },
    staleTime: defaultStaleTime,
  });

  const {
    mutate: saveDashboardMutation,
    isPending: isSaving,
    isSuccess: isSaved,
  } = useMutation({
    mutationFn: (data) => {
      return saveAIDashboard(data);
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ["ai-dashboard"] });
    },
    onError: (error) => {
      Toast.error("Error saving AI Posture dashboard");
    },
  });

  const widgets = useMemo(() => {
    return dashboardData?.widgets || DEFAULT_AI_WIDGETS;
  }, [dashboardData?.widgets]);

  const layouts = useMemo(
    () => dashboardData?.layouts || {},
    [dashboardData?.layouts]
  );

  const generateLayouts = useMemo(
    () => buildSharedLayouts(widgets, layouts),
    [widgets, layouts]
  );

  const debouncedSaveRef = useRef(null);

  useEffect(() => {
    debouncedSaveRef.current = debounce((dashboardData) => {
      if (!mounted) {
        return;
      }
      trackEvent("User re-ordered AI Posture dashboard widgets");
      saveDashboardMutation(dashboardData);
    }, 1000);

    return () => {
      if (debouncedSaveRef.current) {
        debouncedSaveRef.current.cancel();
      }
    };
  }, [saveDashboardMutation, mounted]);

  const onLayoutChange = useCallback(
    (currentLayout, allLayouts) => {
      const updatedDashboardData = {
        layouts: { lg: allLayouts.lg },
        widgets,
      };

      if (debouncedSaveRef.current) {
        debouncedSaveRef.current(updatedDashboardData);
      }
    },
    [widgets, mounted]
  );

  return {
    widgets,
    mounted,
    layouts,
    generateLayouts,
    isLoading,
    isError,
    isSaving,
    isSaved,
    onLayoutChange,
  };
};
